import React, { useState, useRef, useEffect, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useVirtualizer } from "@tanstack/react-virtual";
import { api } from "../lib/api";
import { useI18n } from "../contexts/I18nContext";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Search, Filter, Star, StopCircle } from "lucide-react";
import StatusBadge from "../components/StatusBadge";
import { useModules } from "../contexts/ModulesContext";
import { toast } from "sonner";

const CONTRACTS = ["CDI", "CDD", "Stage", "Alternance", "Freelance"];
const PRESENCE = ["Present", "Remote", "Leave", "Offboarding"];

function useDebounce(v, ms = 300) {
  const [d, setD] = useState(v);
  useEffect(() => { const id = setTimeout(() => setD(v), ms); return () => clearTimeout(id); }, [v, ms]);
  return d;
}

export default function UserRecords() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const { isEnabled } = useModules();
  const multiRecord = isEnabled("multi_record");
  const [search, setSearch] = useState("");
  const [contractFilter, setContractFilter] = useState("all");
  const [activeOnly, setActiveOnly] = useState(false);
  const [mainOnly, setMainOnly] = useState(false);
  const debounced = useDebounce(search, 300);
  const scrollRef = useRef(null);

  const { data: users } = useQuery({
    queryKey: ["users-lookup"],
    queryFn: async () => (await api.get("/users", { params: { limit: 5000 } })).data.data,
  });
  const usersMap = useMemo(() => Object.fromEntries((users || []).map((u) => [u.id, u])), [users]);

  const { data, isLoading } = useQuery({
    queryKey: ["user-records", debounced, contractFilter, activeOnly, mainOnly],
    queryFn: async () => {
      const { data } = await api.get("/user-records", { params: { limit: 5000, q: debounced || undefined } });
      let rows = data.data;
      if (contractFilter !== "all") rows = rows.filter((r) => r.contract_type === contractFilter);
      if (activeOnly) rows = rows.filter((r) => !r.end_date);
      if (mainOnly) rows = rows.filter((r) => r.is_main);
      return rows;
    },
  });

  const rows = data || [];
  // Count records per user to flag multi-fiche rows
  const recordCountByUser = useMemo(() => {
    const m = {};
    (rows || []).forEach((r) => { m[r.user_id] = (m[r.user_id] || 0) + 1; });
    return m;
  }, [rows]);

  const virt = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 48,
    overscan: 10,
  });

  const [editing, setEditing] = useState({ id: null, field: null });
  const commit = async (id, field, value) => {
    try {
      await api.patch(`/user-records/${id}`, { [field]: value });
      qc.invalidateQueries({ queryKey: ["user-records"] });
      toast.success("Mis à jour");
    } catch { toast.error("Erreur"); }
    setEditing({ id: null, field: null });
  };

  return (
    <div className="p-8 max-w-[1600px] mx-auto" data-testid="user-records-page">
      <div className="mb-6">
        <h1 className="font-heading text-4xl font-bold tracking-tight text-warm-dark">{t("user_records")}</h1>
        <p className="text-sm text-warm-dark/60 mt-1">{rows.length.toLocaleString()} fiche(s)</p>
      </div>

      <Card className="border-sand shadow-soft overflow-hidden">
        <div className="px-4 py-3 border-b border-sand flex items-center gap-3 bg-cream/40">
          <div className="relative flex-1 max-w-md">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-warm-dark/50" />
            <Input placeholder={t("search")} value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 h-9" data-testid="records-search" />
          </div>
          <Filter size={14} className="text-warm-dark/50" />
          <Select value={contractFilter} onValueChange={setContractFilter}>
            <SelectTrigger className="h-9 w-44" data-testid="records-contract-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("all")}</SelectItem>
              {CONTRACTS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <label className="flex items-center gap-2 text-sm text-warm-dark/80 cursor-pointer">
            <input type="checkbox" checked={activeOnly} onChange={(e) => setActiveOnly(e.target.checked)} data-testid="records-active-only" />
            Actifs uniquement
          </label>
          {multiRecord && (
            <label className="flex items-center gap-2 text-sm text-warm-dark/80 cursor-pointer">
              <input type="checkbox" checked={mainOnly} onChange={(e) => setMainOnly(e.target.checked)} data-testid="records-main-only" />
              Principales uniquement
            </label>
          )}
        </div>

        <div className="grid grid-cols-[32px_1.5fr_1.3fr_110px_120px_100px_90px_110px_90px] gap-3 px-4 py-2.5 bg-cream border-b border-sand text-xs font-semibold uppercase tracking-wider text-warm-dark/60">
          <div></div>
          <div>Utilisateur</div>
          <div>{t("title")}</div>
          <div>{t("contract_type")}</div>
          <div>{t("presence_state")}</div>
          <div>{t("time_type")}</div>
          <div>{t("working_rate")}</div>
          <div>{t("start_date")}</div>
          <div className="text-right">Actions</div>
        </div>

        <div ref={scrollRef} className="h-[calc(100vh-290px)] overflow-auto" data-testid="records-virtual-list">
          {isLoading ? <div className="p-10 text-center text-warm-dark/50 text-sm">{t("loading")}</div> : rows.length === 0 ? (
            <div className="p-10 text-center text-warm-dark/50 text-sm">{t("no_results")}</div>
          ) : (
            <div style={{ height: virt.getTotalSize(), position: "relative" }}>
              {virt.getVirtualItems().map((vi) => {
                const r = rows[vi.index];
                const u = usersMap[r.user_id];
                const isEditingTitle = editing.id === r.id && editing.field === "title";
                const userRecCount = recordCountByUser[r.user_id] || 1;
                const terminate = async () => {
                  try {
                    await api.patch(`/user-records/${r.id}`, { end_date: new Date().toISOString().slice(0, 10) });
                    qc.invalidateQueries({ queryKey: ["user-records"] });
                    toast.success("Fiche terminée");
                  } catch { toast.error("Erreur"); }
                };
                const setAsMain = async () => {
                  try {
                    await api.post(`/user-records/${r.id}/set-main`);
                    qc.invalidateQueries({ queryKey: ["user-records"] });
                    toast.success("Fiche définie comme principale");
                  } catch { toast.error("Erreur"); }
                };
                return (
                  <div key={r.id} className="virt-row grid grid-cols-[32px_1.5fr_1.3fr_110px_120px_100px_90px_110px_90px] gap-3 px-4 items-center text-sm"
                    style={{ position: "absolute", top: 0, left: 0, right: 0, transform: `translateY(${vi.start}px)`, height: vi.size }}
                    data-testid={`record-row-${r.id}`}>
                    <div className="flex items-center justify-center">
                      {r.is_main ? (
                        <Star size={14} className="text-amber-500 fill-amber-400" data-testid={`main-star-${r.id}`} />
                      ) : (
                        <span className="text-warm-dark/30 text-xs">·</span>
                      )}
                    </div>
                    <div className="truncate">
                      <div className="flex items-center gap-1.5">
                        <span className="text-warm-dark font-medium truncate">{u?.display_name || r.user_id.slice(0, 8)}</span>
                        {userRecCount > 1 && (
                          <span className="inline-flex items-center rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200 px-1.5 py-0 text-[10px] font-semibold" data-testid={`multi-badge-${r.id}`}>
                            {userRecCount} fiches
                          </span>
                        )}
                      </div>
                      <div className="mono text-xs text-warm-dark/60">{u?.login}</div>
                    </div>
                    <div className="truncate" onClick={() => setEditing({ id: r.id, field: "title" })}>
                      {isEditingTitle ? (
                        <Input autoFocus defaultValue={r.title || ""} className="h-7"
                          onBlur={(e) => commit(r.id, "title", e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") commit(r.id, "title", e.currentTarget.value); if (e.key === "Escape") setEditing({ id: null, field: null }); }} />
                      ) : <span className="text-warm-dark/80 cursor-pointer hover:bg-sand rounded px-1.5 py-0.5 -mx-1.5">{r.title || "—"}</span>}
                    </div>
                    <div>
                      <Select value={r.contract_type} onValueChange={(v) => commit(r.id, "contract_type", v)}>
                        <SelectTrigger className="h-7 text-xs border-0 hover:bg-sand shadow-none p-1"><SelectValue /></SelectTrigger>
                        <SelectContent>{CONTRACTS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Select value={r.presence_state} onValueChange={(v) => commit(r.id, "presence_state", v)}>
                        <SelectTrigger className="h-7 text-xs border-0 hover:bg-sand shadow-none p-1"><SelectValue /></SelectTrigger>
                        <SelectContent>{PRESENCE.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div><StatusBadge kind="time_type" value={r.time_type} /></div>
                    <div className="text-warm-dark/80 mono text-xs">{r.working_rate}%</div>
                    <div className="text-warm-dark/60 mono text-xs">{r.start_date || "—"}</div>
                    <div className="flex items-center gap-1 justify-end">
                      {multiRecord && !r.is_main && (
                        <button onClick={setAsMain} className="text-[10px] text-warm-dark/60 hover:text-amber-600" title="Définir comme principale" data-testid={`btn-setmain-${r.id}`}>
                          <Star size={13} />
                        </button>
                      )}
                      {!r.end_date && (
                        <button onClick={terminate} className="text-[10px] text-warm-dark/60 hover:text-red-600" title="Terminer la fiche" data-testid={`btn-end-${r.id}`}>
                          <StopCircle size={13} />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
